import React, { useEffect, useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import { TourContext } from "../context/TourContext";
import axios from "axios";
import {
  Card,
  Alert,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Button,
  Spin,
  Steps,
  Modal,
  Grid,
  Tooltip,
  Select,
  Space,
} from "antd";
import {
  DollarOutlined,
  ScheduleOutlined,
  AlertOutlined,
  ArrowUpOutlined,
  ArrowRightOutlined,
  TransactionOutlined,
  ReloadOutlined,
  FileAddOutlined,
  UserAddOutlined,
  BankOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import { Line, Pie } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "./Styles.css";
import "./homeDashboard.css";
// Using Ant Design Modal for touring
// import Joyride from "react-joyride";

// Custom theme colors for Zambian fintech
const ZAMBIA_THEME = {
  primary: "#078930", // Zambian green
  secondary: "#EF3340", // Zambian red
  accent: "#FFB81C", // Gold accent
  neutral: "#2D2D2D", // Dark neutral
};

const { Title, Text } = Typography;
const { Step } = Steps;

const Home = () => {
  const { company, user } = useContext(AppContext);
  const [loans, setLoans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [loanChartYear, setLoanChartYear] = useState(moment().year());
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Access the tour context
  const {
    runTour,
    tourContent,
    currentTourType,
    startTour,
    endTour,
    isTourCompleted,
    resetAllTours,
  } = useContext(TourContext);

  // State for onboarding modal
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  // Enhanced status configuration
  const statusConfig = {
    active: {
      color: ZAMBIA_THEME.primary,
      icon: <ArrowUpOutlined className="status-icon" />,
      label: "Active Loans",
    },
    overdue: {
      color: ZAMBIA_THEME.secondary,
      icon: <AlertOutlined className="status-icon" />,
      label: "Overdue Loans",
    },
    pending: {
      color: ZAMBIA_THEME.accent,
      icon: <ScheduleOutlined className="status-icon" />,
      label: "Pending Approval",
    },
    /* app: {
      color: "#722ED1",
      icon: <MobileOutlined className="status-icon" />,
      label: "App Applications",
    }, */
    defaulted: {
      color: "#000000",
      icon: <AlertOutlined className="status-icon" />,
      label: "Defaulted Loans",
    },
  };

  // Function to fetch data
  const fetchData = async (isRefreshing = false) => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }

      const sixMonthsAgo = moment().subtract(6, "months").format("YYYY-MM-DD");

      const [transactionsRes, loansRes] = await Promise.all([
        axios.get(`https://zedlenders.pickmesms.com/items/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              company: company?.id,
              transaction_date: { _gte: sixMonthsAgo },
            },
            sort: "-transaction_date",
            limit: -1,
          },
        }),
        axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[company]=${company?.id}&limit=-1`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      setTransactions(transactionsRes.data.data);
      setLoans(loansRes.data.data);
      setError(null);

      // Check if this appears to be a new user
      if (loansRes.data.data.length === 0) {
        // Show onboarding for users with no loans
        setIsNewUser(true);

        // Only show the modal if the onboarding tour has not been completed
        if (!isTourCompleted("onboarding")) {
          setShowOnboardingModal(true);
        }
      }
    } catch (err) {
      setError("Failed to load data. Please check your connection.");
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // State for refresh loading
  const [refreshing, setRefreshing] = useState(false);

  // Handle refresh button click
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData(true);
    setRefreshing(false);
  };

  // Data fetching with error handling
  useEffect(() => {
    fetchData();
  }, [company?.id, token]);

  // Fetch expenses for selected year
  const fetchExpenses = async (year) => {
    try {
      setExpenseLoading(true);
      const res = await axios.get(
        `https://zedlenders.pickmesms.com/items/transactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              company: company?.id,
              transaction_type: "Operational Cost",
              transaction_status: "Completed",
              transaction_date: {
                _gte: `${year}-01-01`,
                _lte: `${year}-12-31`,
              },
            },
            limit: -1,
          },
        },
      );
      setExpenses(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch expenses:", err);
      setExpenses([]);
    } finally {
      setExpenseLoading(false);
    }
  };

  useEffect(() => {
    if (company?.id && token) {
      fetchExpenses(selectedYear);
    }
  }, [company?.id, token, selectedYear]);

  // Handle Joyride callbacks
  const handleJoyrideCallback = (data) => {
    const { status, type } = data;

    if (status === "finished" || status === "skipped") {
      endTour(true);
    }
  };

  // Calculate metrics
  const portfolioMetrics = {
    totalLoans: loans.length,
    totalDisbursed: loans.reduce((sum, loan) => sum + loan.amount, 0),
    activeLoans: loans.filter((loan) => loan.loan_status === 7).length,
    overdueLoans: loans.filter((loan) => loan.loan_status === 8).length,
    pendingLoans: loans.filter((loan) => loan.loan_status === 1).length,
    appLoans: loans.filter((loan) => loan.loan_status === 12).length,
    defaultedLoans: loans.filter((loan) => loan.loan_status === 9).length,
    recoveryRate:
      loans.length > 0
        ? (
            (loans.reduce((sum, loan) => sum + (loan.paid_amount || 0), 0) /
              loans.reduce((sum, loan) => sum + loan.amount, 0)) *
            100
          ).toFixed(1)
        : 0,
  };

  // Enhanced chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right", labels: { color: ZAMBIA_THEME.neutral } },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.label}: ${context.raw} loan${context.raw !== 1 ? "s" : ""}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: ZAMBIA_THEME.neutral,
          callback: (value) => `ZMW ${value.toLocaleString()}`,
        },
      },
      x: { ticks: { color: ZAMBIA_THEME.neutral } },
    },
  };

  // Process expenses by month
  const MONTH_COLORS = [
    "#078930",
    "#EF3340",
    "#FFB81C",
    "#1890ff",
    "#722ed1",
    "#13c2c2",
    "#fa541c",
    "#52c41a",
    "#eb2f96",
    "#faad14",
    "#2f54eb",
    "#a0d911",
  ];
  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const expensesByMonth = expenses.reduce((acc, t) => {
    const month = moment(t.transaction_date).month();
    acc[month] = (acc[month] || 0) + (t.amount || 0);
    return acc;
  }, {});

  const totalExpenses = Object.values(expensesByMonth).reduce(
    (s, v) => s + v,
    0,
  );

  const expenseChartLabels = [];
  const expenseChartData = [];
  const expenseChartColors = [];
  Object.keys(expensesByMonth)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((month) => {
      expenseChartLabels.push(MONTH_NAMES[month]);
      expenseChartData.push(expensesByMonth[month]);
      expenseChartColors.push(MONTH_COLORS[month]);
    });

  const expensePieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: ZAMBIA_THEME.neutral,
          padding: 12,
          generateLabels: (chart) => {
            const data = chart.data;
            return data.labels.map((label, i) => {
              const value = data.datasets[0].data[i];
              const pct =
                totalExpenses > 0
                  ? ((value / totalExpenses) * 100).toFixed(1)
                  : 0;
              return {
                text: `${label}: ZMW ${value.toLocaleString()} (${pct}%)`,
                fillStyle: data.datasets[0].backgroundColor[i],
                strokeStyle: "#fff",
                lineWidth: 2,
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const pct =
              totalExpenses > 0
                ? ((value / totalExpenses) * 100).toFixed(1)
                : 0;
            return `${context.label}: ZMW ${value.toLocaleString()} (${pct}%)`;
          },
        },
      },
    },
  };

  // Year options for dropdown
  const currentYear = moment().year();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Process loans by month for the selected loan chart year
  const loansByMonth = loans
    .filter((loan) => loan.approval_date && moment(loan.approval_date).year() === loanChartYear)
    .reduce((acc, loan) => {
      const month = moment(loan.approval_date).month();
      acc[month] = (acc[month] || 0) + (loan.amount || 0);
      return acc;
    }, {});

  const loanLineData = MONTH_NAMES.map((_, i) => loansByMonth[i] || 0);

  const loanLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `ZMW ${context.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: ZAMBIA_THEME.neutral,
          callback: (value) => `ZMW ${value.toLocaleString()}`,
        },
      },
      x: { ticks: { color: ZAMBIA_THEME.neutral } },
    },
  };

  if (error) return <Alert message={error} type="error" showIcon />;
  if (loading)
    return (
      <div className="home-dashboard-loading loading-spinner">
        <Spin size="large" />
      </div>
    );

  // Get current user name from context or localStorage
  const userName =
    user?.first_name || localStorage.getItem("firstname") || "User";

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  // Navigation handlers for onboarding flow
  const goToLoanProducts = () => {
    navigate("/loanProducts");
    startTour("loanProducts");
  };

  const goToBorrowers = () => {
    navigate("/borrowers");
    startTour("borrowers");
  };

  const goToAddLoan = () => {
    navigate("/addLoan");
    startTour("loanApplication");
  };

  // Navigation handlers for loan status cards
  const handleCardClick = (status) => {
    switch (status) {
      case "active":
        navigate("/loans/7");
        break;
      case "overdue":
        navigate("/loans/8");
        break;
      case "pending":
        navigate("/loans/1");
        break;
      case "app":
        navigate("/loans/12");
        break;
      case "defaulted":
        navigate("/loans/9");
        break;
      default:
        break;
    }
  };

  return (
    <div className="home-dashboard-shell" role="main">
      {/* Custom modal-based tour */}
      {runTour && tourContent && (
        <Modal
          title={tourContent.title || "Tour Guide"}
          open={runTour}
          onCancel={() => endTour(false)}
          footer={[
            <Button key="skip" onClick={() => endTour(false)}>
              Skip Tour
            </Button>,
            <Button key="next" type="primary" onClick={() => endTour(true)}>
              Got it!
            </Button>,
          ]}
          width={500}
        >
          <div style={{ padding: "10px 0" }}>
            <Alert
              message={tourContent.message || "Welcome to the guided tour!"}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Text>Follow these steps:</Text>
            <ul>
              {tourContent.details?.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      {/* Onboarding Modal for New Users */}
      <Modal
        title={<div style={{ textAlign: "center" }}>Welcome!</div>}
        visible={showOnboardingModal}
        onCancel={() => setShowOnboardingModal(false)}
        footer={null}
        width={600}
      >
        <div style={{ padding: "20px 0" }}>
          <Title level={4} style={{ textAlign: "center", marginBottom: 30 }}>
            Let's help you get started
          </Title>

          <Steps direction="vertical" current={-1}>
            <Step
              title="Set Up Loan Products"
              description="Configure the loan products you'll offer to borrowers"
              icon={<BankOutlined />}
            />
            <Step
              title="Add Borrowers"
              description="Add borrowers to your system"
              icon={<UserAddOutlined />}
            />
            <Step
              title="Create Loan Applications"
              description="Create and manage loan applications"
              icon={<FileAddOutlined />}
            />
          </Steps>

          <div style={{ marginTop: 30, textAlign: "center" }}>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setShowOnboardingModal(false);
                startTour("onboarding");
              }}
            >
              Start Guided Tour <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Modal>

      <Card className="welcome-banner dashboard-welcome home-dashboard-hero" bordered={false} style={{ marginBottom: 24 }}>
        <Row align="middle" gutter={[16, 16]} wrap>
          <Col xs={24} md={16}>
            <Space align="center" size={16} wrap>
              {company?.logo ? (
                <Tooltip title="Your company logo. Click settings to update.">
                  <img src={company.logo} alt="" className="home-dashboard-hero__avatar" />
                </Tooltip>
              ) : (
                <Tooltip title="Upload a logo in settings for a personalized dashboard.">
                  <div className="home-dashboard-hero__avatar-fallback" aria-hidden>
                    <DollarOutlined />
                  </div>
                </Tooltip>
              )}
              <div>
                <Tooltip title="Personalized greeting based on the time of day.">
                  <Title level={2} style={{ margin: 0, marginBottom: 4, color: "#0f172a", fontWeight: 700, letterSpacing: "-0.03em", fontSize: isMobile ? 22 : 26 }}>
                    {getGreeting()}, {userName}
                  </Title>
                </Tooltip>
                <Tooltip title="Your main dashboard — portfolio health at a glance.">
                  <Text type="secondary" style={{ fontSize: isMobile ? 14 : 15 }}>
                    {company?.company_name || "KwachaPlus"} · Portfolio overview
                  </Text>
                </Tooltip>
              </div>
            </Space>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: isMobile ? "left" : "right" }}>
            <Space direction="vertical" size={12} style={{ width: "100%", alignItems: isMobile ? "flex-start" : "flex-end" }}>
              <Space wrap size={10}>
                <Tooltip title="Refresh dashboard data">
                  <Button icon={<ReloadOutlined spin={refreshing} />} onClick={handleRefresh}>
                    Refresh
                  </Button>
                </Tooltip>
                {isNewUser && (
                  <Tooltip title="Start the setup guide">
                    <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => setShowOnboardingModal(true)}>
                      Setup Guide
                    </Button>
                  </Tooltip>
                )}
              </Space>
              <Tooltip title="Get the KwachaPlus mobile app on Google Play">
                <a
                  href="https://play.google.com/store/apps/details?id=com.appexpress.kwachaplus"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get KwachaPlus on Google Play"
                >
                  <img
                    src="/images/google-play-badge.png"
                    alt="Get it on Google Play"
                    style={{ height: 48, width: "auto" }}
                  />
                </a>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Onboarding Steps for New Users */}
      {isNewUser && (
        <Card className="home-onboarding-panel" bordered={false} style={{ marginBottom: 24 }}>
          <Title level={4} style={{ marginBottom: 8 }}>
            Get Started with KwachaPlus
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
            Follow these steps to set up your lending business
          </Text>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Tooltip title="Define the types of loans your business offers to borrowers.">
                <Card
                  className="onboarding-loan-products home-onboarding-step-card"
                  hoverable
                  onClick={goToLoanProducts}
                  bordered={false}
                  style={{
                    height: "100%",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  <BankOutlined
                    style={{
                      fontSize: 32,
                      color: ZAMBIA_THEME.primary,
                      marginBottom: 12,
                    }}
                  />
                  <Title level={4}>1. Create Loan Products</Title>
                  <Text>
                    Define the types of loans your business offers to borrowers
                  </Text>
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary">
                      Set Up Products <ArrowRightOutlined />
                    </Button>
                  </div>
                </Card>
              </Tooltip>
            </Col>

            <Col xs={24} md={8}>
              <Tooltip title="Add borrowers to your system with their personal information.">
                <Card
                  className="onboarding-borrowers home-onboarding-step-card"
                  hoverable
                  onClick={goToBorrowers}
                  bordered={false}
                  style={{
                    height: "100%",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  <UserAddOutlined
                    style={{
                      fontSize: 32,
                      color: ZAMBIA_THEME.primary,
                      marginBottom: 12,
                    }}
                  />
                  <Title level={4}>2. Add Borrowers</Title>
                  <Text>
                    Add borrowers to your system with their personal information
                  </Text>
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary">
                      Add Borrowers <ArrowRightOutlined />
                    </Button>
                  </div>
                </Card>
              </Tooltip>
            </Col>

            <Col xs={24} md={8}>
              <Tooltip title="Create and manage loan applications for your borrowers.">
                <Card
                  className="onboarding-loan-application home-onboarding-step-card"
                  hoverable
                  onClick={goToAddLoan}
                  bordered={false}
                  style={{
                    height: "100%",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  <FileAddOutlined
                    style={{
                      fontSize: 32,
                      color: ZAMBIA_THEME.primary,
                      marginBottom: 12,
                    }}
                  />
                  <Title level={4}>3. Create Loans</Title>
                  <Text>Create loan applications for your borrowers</Text>
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary">
                      Create Loans <ArrowRightOutlined />
                    </Button>
                  </div>
                </Card>
              </Tooltip>
            </Col>
          </Row>
        </Card>
      )}

      {/* <header className="dashboard-header">
        <Row
          justify={isMobile ? "center" : "space-between"}
          align="middle"
          style={{ width: "100%" }}
          gutter={[16, 16]}
        >
          <Col xs={24} md={12}>
            <Text
              type="secondary"
              style={{ fontSize: isMobile ? "14px" : "16px" }}
            >
              {company?.name
                ? `${company.name}'s Financial Summary`
                : "Financial Insights"}
            </Text>
          </Col>
          <Col
            xs={24}
            md={12}
            style={{ textAlign: isMobile ? "center" : "right" }}
          >
            <Text
              type="secondary"
              style={{ fontSize: isMobile ? "12px" : "14px" }}
            >
              Last updated:{" "}
              {moment().format(
                isMobile ? "DD MMM, h:mm A" : "DD MMM YYYY, h:mm A"
              )}
            </Text>
          </Col>
        </Row>
      </header> */}

      <Row gutter={[16, 16]} className="metric-row stats-row">
        {Object.entries(statusConfig).map(([key, { color, icon, label }]) => {
          const raw = portfolioMetrics[`${key}Loans`] ?? 0;
          return (
            <Col xs={12} sm={12} md={6} key={key}>
              <Tooltip title={`View all ${label.toLowerCase()} in your portfolio.`}>
                <Card
                  className="metric-card home-stat-card"
                  bordered={false}
                  hoverable
                  onClick={() => handleCardClick(key)}
                  bodyStyle={{ padding: "20px 22px", cursor: "pointer" }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#64748b", marginBottom: 8 }}>{label}</div>
                      <div
                        style={{
                          fontSize: isMobile ? 22 : 28,
                          fontWeight: 700,
                          color,
                          lineHeight: 1.15,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {raw}
                      </div>
                    </div>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `${color}18`,
                        color,
                        fontSize: 20,
                      }}
                    >
                      {icon}
                    </div>
                  </div>
                </Card>
              </Tooltip>
            </Col>
          );
        })}
      </Row>

      <Row gutter={[20, 20]} className="chart-row">
        <Col xs={24} xl={12}>
          <Tooltip title="Pie chart showing the distribution of your loan portfolio by status.">
            <Card
              title="Portfolio Distribution"
              className="chart-card home-chart-card"
              bordered={false}
              extra={<Tag color={ZAMBIA_THEME.primary}>Live</Tag>}
            >
              <div className="home-chart-wrap">
              <Pie
                data={{
                  labels: ["Active", "Overdue", "Defaulted", "Pending" /* , "App" */],
                  datasets: [
                    {
                      data: [
                        portfolioMetrics.activeLoans,
                        portfolioMetrics.overdueLoans,
                        portfolioMetrics.defaultedLoans,
                        portfolioMetrics.pendingLoans,
                        /* portfolioMetrics.appLoans, */
                      ],
                      backgroundColor: [
                        statusConfig.active.color,
                        statusConfig.overdue.color,
                        statusConfig.defaulted.color,
                        statusConfig.pending.color,
                        /* statusConfig.app.color, */
                      ],
                      borderColor: "#fff",
                      borderWidth: 2,
                    },
                  ],
                }}
                options={chartOptions}
              />
              </div>
            </Card>
          </Tooltip>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            title="Amount Loaned by Month"
            className="chart-card home-chart-card"
            bordered={false}
            extra={
              <Select
                value={loanChartYear}
                onChange={setLoanChartYear}
                style={{ width: 100 }}
              >
                {yearOptions.map((y) => (
                  <Select.Option key={y} value={y}>
                    {y}
                  </Select.Option>
                ))}
              </Select>
            }
          >
            <div className="home-chart-wrap">
            <Line
              data={{
                labels: MONTH_NAMES.map((m) => m.substring(0, 3)),
                datasets: [
                  {
                    label: "Amount Loaned",
                    data: loanLineData,
                    borderColor: ZAMBIA_THEME.primary,
                    backgroundColor: ZAMBIA_THEME.primary + "20",
                    borderWidth: 2,
                    fill: true,
                    tension: 0.3,
                    pointBackgroundColor: ZAMBIA_THEME.primary,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                  },
                ],
              }}
              options={loanLineOptions}
            />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]} style={{ marginTop: 8 }}>
        <Col xs={24}>
          <Card
            title="Expense Distribution by Month"
            className="chart-card home-chart-card"
            bordered={false}
            extra={
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 100 }}
              >
                {yearOptions.map((y) => (
                  <Select.Option key={y} value={y}>
                    {y}
                  </Select.Option>
                ))}
              </Select>
            }
          >
            {expenseLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin />
              </div>
            ) : expenseChartData.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Text type="secondary">
                  No expenses recorded for {selectedYear}
                </Text>
              </div>
            ) : (
              <div className="home-chart-wrap" style={{ margin: "0 auto" }}>
                <Pie
                  data={{
                    labels: expenseChartLabels,
                    datasets: [
                      {
                        data: expenseChartData,
                        backgroundColor: expenseChartColors,
                        borderColor: "#fff",
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={expensePieOptions}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title="Recent Transactions"
        className="transaction-card home-transactions-card"
        bordered={false}
        extra={[
          <Button key="view-all" type="primary" onClick={() => navigate("/transactions")} icon={<TransactionOutlined />}>
            View all
          </Button>,
        ]}
      >
        <Table
          columns={[
            {
              title: "Date",
              dataIndex: "transaction_date",
              render: (date) => moment(date).format("DD MMM YYYY"),
              sorter: (a, b) =>
                moment(a.transaction_date) - moment(b.transaction_date),
            },
            {
              title: "Type",
              dataIndex: "transaction_type",
              render: (type) => <Tag color={ZAMBIA_THEME.primary}>{type}</Tag>,
            },
            {
              title: "Amount (ZMW)",
              dataIndex: "amount",
              render: (amount) =>
                amount.toLocaleString("en-ZA", {
                  style: "currency",
                  currency: "ZMW",
                }),
            },
            {
              title: "Status",
              dataIndex: "transaction_status",
              render: (status) => (
                <Tag
                  color={
                    statusConfig[status?.toLowerCase()]?.color ||
                    ZAMBIA_THEME.neutral
                  }
                >
                  {status || "Unknown"}
                </Tag>
              ),
            },
          ]}
          dataSource={transactions.slice(0, 8)}
          rowKey="id"
          pagination={false}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default Home;
